// eslint-disable-next-line prettier/prettier
import { Toast, UIProvider } from '@asgard/ui-library';
import { UserProvider } from '@auth0/nextjs-auth0/client';
import { ErrorBoundary } from '@components/shared/ErrorBoundary';
import { SegmentScript } from '@components/shared/SegmentScript';
import { GlobalStyles } from '@components/styles/GlobalStyles';
import { config as FAConfig, library } from '@fortawesome/fontawesome-svg-core';
import '@fortawesome/fontawesome-svg-core/styles.css';
import theme from '@theme/index';
import { ENV } from '@utils/env-vars';
import { faManfred } from '@utils/icons';
import { renderSegmentSnippet } from '@utils/segment';
import flagsmith from 'flagsmith';
import { FlagsmithProvider } from 'flagsmith/react';
import { IState } from 'flagsmith/types';
import { appWithTranslation } from 'next-i18next';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import 'reflect-metadata';
import { ThemeProvider } from 'styled-components';
import { SWRConfig } from 'swr';

// See: https://fontawesome.com/docs/web/use-with/react/use-with
FAConfig.autoAddCss = false;

library.add(faManfred);

function App({
  Component,
  pageProps
}: AppProps & {
  pageProps:
    | {
        fallback?: { [key: string]: unknown };
        flagsmithState?: IState<string, string>;
      }
    | undefined;
}): JSX.Element {
  const router = useRouter();

  return (
    <>
      <Toast position="bottom-right" />

      <Head>
        <title>Talent Engine - Manfred</title>

        <meta name="viewport" content="width=device-width, initial-scale=1.0" />

        <meta property="og:locale" content={router.locale} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Manfred" />
      </Head>

      <SegmentScript />

      <ErrorBoundary>
        <ThemeProvider theme={theme}>
          <UIProvider theme={theme}>
            <SWRConfig
              value={{
                fallback: pageProps.fallback
              }}
            >
              <FlagsmithProvider
                flagsmith={flagsmith}
                {...(typeof window !== 'undefined' && {
                  options: {
                    environmentID: ENV.flagsmithEnvironmentKey
                  }
                })}
                serverState={
                  'flagsmithState' in pageProps
                    ? (pageProps.flagsmithState as IState<string, string>)
                    : undefined
                }
              >
                <UserProvider>
                  {ENV.segmentApiKey && (
                    <Script
                      id="segment-script"
                      dangerouslySetInnerHTML={{
                        __html: renderSegmentSnippet()
                      }}
                    />
                  )}

                  <GlobalStyles />

                  <Component {...pageProps} />
                </UserProvider>
              </FlagsmithProvider>
            </SWRConfig>
          </UIProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </>
  );
}

export default appWithTranslation(App);
